<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, ref } from 'vue'
import StudyCourseCard from '@/components/portal/StudyCourseCard.vue'
import StudyLessons from '@/components/portal/StudyLessons.vue'
import CompletionStatus from '@/components/CompletionStatus.vue'
// 학습자 본인 수강 목록(나의강의실 진행중인 과정·학습종료 과정). 레거시 #contentsArea 카드 목록을 따른다.
// 진행중 카드는 열기 막대가 없고, 학습종료 카드는 열면 차시별 학습 이력(수강하기·복습)과 수료 상태(수료증 출력)를 보인다.
const props = defineProps({ request: { type: Function, required: true }, messageOf: { type: Function, required: true }, group: { type: String, default: 'CURRENT' } })
const emit = defineEmits(['loaded'])
const BASE = '/api/user/enrollments'
const rows = ref([]), total = ref(0), page = ref(1), loading = ref(false), error = ref(''), openId = ref(null)
const ended = computed(() => props.group === 'ENDED')
const emptyText = computed(() => ended.value ? '강의가 없습니다.' : '진행 중인 과정이 없습니다.')
let alive = true, listEpoch = 0
onBeforeUnmount(() => { alive = false; listEpoch++ })
async function load(reset = false) {
  if (reset) page.value = 1
  // 새 조회가 실패하더라도 이전 목록을 현재 결과처럼 노출하지 않는다.
  const epoch = ++listEpoch; loading.value = true; error.value = ''; rows.value = []; openId.value = null; total.value = 0
  try {
    const data = await props.request(`${BASE}?${new URLSearchParams({ page: page.value, size: 20, group: props.group })}`)
    if (alive && epoch === listEpoch) { rows.value = data.items; total.value = data.total; emit('loaded', data.total) }
  } catch (cause) { if (alive && epoch === listEpoch) { error.value = props.messageOf(cause); emit('loaded', null) } }
  finally { if (alive && epoch === listEpoch) loading.value = false }
}
// 노란 열기/닫기 막대: 한 번에 한 과정만 연다.
function toggle(item) { openId.value = openId.value === item.enrollId ? null : item.enrollId }
// 수료증 출력은 과정을 열고 수료 상태(수료증) 영역으로 이동한다.
async function certificate(item) {
  openId.value = item.enrollId
  await nextTick(); document.querySelector(`[data-testid="enrollment-${item.enrollId}"] .completion-status`)?.scrollIntoView({ behavior: 'smooth', block: 'start' })
}
const cards = {}
const cardRef = id => el => { if (el) cards[id] = el; else delete cards[id] }
const completionLoaded = (id, data) => cards[id]?.refresh({ completionData: data, evaluation: false })
onMounted(() => load())
</script>
<template>
  <div class="study-list">
    <v-alert v-if="error" role="alert" aria-label="수강 처리 오류" type="error" variant="tonal" class="mb-5">{{ error }}</v-alert>
    <section class="study-list-panel" aria-label="수강 목록">
      <v-progress-linear v-if="loading" indeterminate aria-label="수강 조회 중" class="mb-4" />
      <p v-if="!loading && !error && !rows.length" class="study-empty">{{ emptyText }}</p>
      <StudyCourseCard v-for="item in rows" :key="item.enrollId" :ref="cardRef(item.enrollId)" :item="item" :kind="ended ? 'ended' : 'current'" :open="openId === item.enrollId" :disabled="loading" :request="request" :message-of="messageOf" @toggle="toggle(item)" @certificate="certificate(item)">
        <section class="study-detail-body" aria-label="수강 상세">
          <StudyLessons :key="`lessons-${item.enrollId}`" :enroll-id="item.enrollId" :request="request" :message-of="messageOf" />
          <CompletionStatus :key="`completion-${item.enrollId}`" :enroll-id="item.enrollId" :request="request" :message-of="messageOf" @loaded="data => completionLoaded(item.enrollId, data)" />
        </section>
      </StudyCourseCard>
      <div v-if="total > 20" class="study-pagination"><button type="button" class="study-page" :disabled="loading || page === 1" @click="page--; load()">이전</button><span>{{ page }} / {{ Math.ceil(total / 20) }}</span><button type="button" class="study-page" :disabled="loading || page * 20 >= total" @click="page++; load()">다음</button></div>
    </section>
  </div>
</template>
<style scoped>
.study-list{min-width:0;color:#666}
.study-list-panel{min-width:0}
/* 레거시 li.noList: 120px, #efefef, 1px #ccc, 15px 굵게 */
.study-empty{height:120px;margin:0 0 10px;border:1px solid #ccc;background:#efefef;color:#666;font-size:15px;font-weight:700;line-height:120px;text-align:center}
.study-detail-body :deep(.completion-status){margin:12px 0 0;border-color:#bfc3c4;border-radius:0}
.study-page{padding:5px 12px;border:1px solid #bfc3c4;border-radius:4px;background:#fff;color:#455467;font-size:13px;line-height:18px}
.study-page:hover:not(:disabled),.study-page:focus-visible{background:#efefef}
.study-page:disabled{opacity:.55}
.study-pagination{display:flex;justify-content:center;align-items:center;gap:12px;margin-top:18px}
@media (max-width:640px){.study-empty{height:auto;padding:40px 10px;line-height:1.5}}
</style>
